import { supabase } from '../lib/supabase';

const SUPABASE_URL = import.meta.env?.VITE_SUPABASE_URL;

// Wilcart branding appended to every SMS (like Square's "Sent via Square")
const WILCART_TAG = 'wilcart.com';

/**
 * Send an SMS via the Twilio Edge Function
 * @param {Object} params
 * @param {string} params.to - Recipient phone number
 * @param {string} params.message - SMS message body
 * @param {string} params.orgId - Organization ID (for logging)
 * @param {string} params.eventType - Event type: 'job_status_change' | 'new_lead' | 'invoice_paid'
 */
export async function sendSMS({ to, message, orgId, eventType }) {
  if (!to || !message) return { success: false, error: 'Missing to or message' };

  try {
    const { data: { session } } = await supabase?.auth?.getSession();
    const token = session?.access_token;

    const res = await fetch(`${SUPABASE_URL}/functions/v1/send-sms`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify({ to, message, org_id: orgId, event_type: eventType }),
    });

    const data = await res?.json();

    if (!res?.ok) {
      console.error('SMS send failed:', data);
      if (orgId) {
        await supabase?.from('sms_logs')?.insert({
          org_id: orgId,
          to_phone: to,
          message,
          event_type: eventType || 'unknown',
          status: 'failed',
          error_message: data?.error || 'Unknown error',
        })?.catch(() => {});
      }
      return { success: false, error: data?.error };
    }

    if (orgId) {
      await supabase?.from('sms_logs')?.insert({
        org_id: orgId,
        to_phone: to,
        message,
        event_type: eventType || 'unknown',
        status: 'sent',
        twilio_sid: data?.messageSid,
      })?.catch(() => {});
    }

    return { success: true, messageSid: data?.messageSid };
  } catch (err) {
    console.error('SMS service error:', err);
    return { success: false, error: err?.message };
  }
}

/**
 * Platform-controlled universal SMS templates.
 * Wilcart defines the message — clients cannot edit these.
 * Every message ends with a subtle Wilcart branding link.
 */

/**
 * Build SMS message for job status change
 */
export function buildJobStatusSMS({ clientName, jobTitle, newStatus, orgName }) {
  const statusLabel = {
    scheduled: 'has been scheduled',
    in_progress: 'is now in progress',
    completed: 'has been completed',
    cancelled: 'has been cancelled',
  }?.[newStatus] || `status updated to ${newStatus}`;

  const biz = orgName ? ` — ${orgName}` : '';
  return `Hi ${clientName || 'there'}, your job "${jobTitle}" ${statusLabel}${biz}. Reply STOP to opt out. Powered by ${WILCART_TAG}`;
}

/**
 * Build SMS message for new lead confirmation
 */
export function buildNewLeadSMS({ leadName, serviceType, orgName }) {
  const biz = orgName ? `${orgName}` : 'our team';
  const service = serviceType ? ` about ${serviceType}` : '';
  return `Hi ${leadName || 'there'}, thanks for reaching out${service}! ${biz} will be in touch shortly. Reply STOP to opt out. Powered by ${WILCART_TAG}`;
}

/**
 * Build SMS message for invoice paid
 */
export function buildInvoicePaidSMS({ clientName, amount, invoiceNumber, orgName }) {
  const biz = orgName ? ` — ${orgName}` : '';
  const amtStr = amount ? `$${amount}` : 'your payment';
  const inv = invoiceNumber ? ` for invoice #${invoiceNumber}` : '';
  return `Hi ${clientName || 'there'}, we received ${amtStr}${inv}. Thank you${biz}! Reply STOP to opt out. Powered by ${WILCART_TAG}`;
}

/**
 * Preview templates (for Settings UI display)
 */
export const SMS_TEMPLATE_PREVIEWS = [
  {
    event: 'job_status_change',
    label: 'Job Status Change',
    icon: 'Briefcase',
    preview: `Hi [Client Name], your job "[Job Title]" has been completed — [Business Name]. Reply STOP to opt out. Powered by ${WILCART_TAG}`,
  },
  {
    event: 'new_lead',
    label: 'New Lead Confirmation',
    icon: 'UserPlus',
    preview: `Hi [Lead Name], thanks for reaching out about [Service]! [Business Name] will be in touch shortly. Reply STOP to opt out. Powered by ${WILCART_TAG}`,
  },
  {
    event: 'invoice_paid',
    label: 'Invoice Paid',
    icon: 'DollarSign',
    preview: `Hi [Client Name], we received $[Amount] for invoice #[Number]. Thank you — [Business Name]! Reply STOP to opt out. Powered by ${WILCART_TAG}`,
  },
];
